import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import { IconButton } from '@eventbrite/eds-icon-button';
import {
    ArrowLeftChunky as ArrowLeftChunkySvg,
    ChevronLeftChunky as ChevronLeftChunkySvg,
} from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import React, { useState } from 'react';
import {
    DISTANCE_FILTER,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
} from '../../../../constants/filters';
import { DistanceFilterPanelHeaderContainer } from '../../containers/DistanceFilterPanelHeaderContainer';
import './FilterPanel.scss';
import FilterPanelDrawers from './FilterPanelDrawers';

const Filters = loadable(() => import('./Filters'));

const MULTISELECT_MAP: { [key: string]: boolean } = {
    languages: true,
};

const getFilterPanelTitle = (
    filterTypeToShow?: $FixMe,
    isDatePickerDrawerVisible?: boolean,
) => {
    let filterTitle = gettext('Filters');

    if (isDatePickerDrawerVisible) {
        return gettext('Pick a date...');
    }

    if (filterTypeToShow) {
        filterTitle =
            FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[filterTypeToShow].label;
    }

    return filterTitle;
};

const SaveButton = ({
    isActive,
    onClick,
}: {
    isActive?: boolean;
    onClick?: Function;
}) => (
    <Button
        size="block"
        style="fill"
        onClick={onClick}
        disabled={!isActive}
        data-testid="filter-panel-mobile--save-btn"
    >
        {gettext('Save')}
    </Button>
);

interface FilterPanelHeaderProps {
    filterTitle?: string | GenericLazyString;
    filterTypeToShow?: $FixMe;
    isDatePickerDrawerVisible?: boolean;
    onCloseDatePickerDrawer?: Function;
    handleDrawerSelect?: Function;
    children?: React.ReactChildren;
    isMobile?: boolean;
    isUsingCurrentLocation?: boolean;
}

const FilterPanelHeader = ({
    filterTitle,
    filterTypeToShow,
    isDatePickerDrawerVisible,
    onCloseDatePickerDrawer,
    handleDrawerSelect,
    children,
    isMobile,
}: FilterPanelHeaderProps) => {
    let headerAction = null;
    let newAction = null;

    if (isDatePickerDrawerVisible) {
        headerAction = (
            <IconButton
                data-testid="filter-title__drawer-close-btn"
                iconType={<ArrowLeftChunkySvg />}
                title="Go back to filter selection"
                onClick={() => onCloseDatePickerDrawer?.(false)}
            />
        );
    }
    // Back button when a drawer is selected
    if (filterTypeToShow && !isDatePickerDrawerVisible) {
        newAction = (
            <Button
                style="none"
                onClick={() => {
                    handleDrawerSelect?.('');
                    if (isDatePickerDrawerVisible) {
                        onCloseDatePickerDrawer?.(false);
                    }
                }}
            >
                <Icon
                    type={<ChevronLeftChunkySvg />}
                    size="xsmall"
                    color="grey-700"
                />
                {gettext('Filters')}
            </Button>
        );
    }

    const classes = {
        container: `filter-title-container ${
            isMobile ? 'filter-title-container--mobile' : ''
        }`,
        title: `filter-title ${isMobile ? 'filter-title--mobile' : ''}`,
    };

    return (
        <header className={classes.container}>
            {headerAction}
            <h3 className={classes.title} data-testid="filter-title--mobile">
                <div className="filter-title__filter-close">{newAction}</div>
                <span data-testid="filter-title--mobile__header">
                    {filterTitle}
                </span>
                {filterTypeToShow === DISTANCE_FILTER ? (
                    <DistanceFilterPanelHeaderContainer />
                ) : null}
            </h3>
            {children}
        </header>
    );
};

const FilterPanelMain = (props: {
    isMobile?: boolean;
    children?: React.ReactElement<any, any>;
}) => (
    <div
        className={classnames('filter-panel', {
            'filter-panel--mobile': props.isMobile,
        })}
        data-testid={props.isMobile ? 'filter-panel--mobile' : 'filter-panel'}
    >
        {props.children}
    </div>
);

const FilterPanelFooter = (props: { children: $FixMe; isMobile?: boolean }) => {
    if (props.children.every((child: $FixMe) => !child === true)) {
        return null;
    }

    return (
        <div
            className={`filter-footer__container ${
                props.isMobile ? 'filter-footer__container--mobile' : ''
            }`}
        >
            <div
                className={`filter-footer ${
                    props.isMobile ? 'filter-footer--mobile' : ''
                }`}
            >
                {props.children}
            </div>
        </div>
    );
};

interface FilterPanelProps extends FilterPanelHeaderProps {
    onCustomDateChange?: Function;
    onFilterChange?: Function;
    filterTypeToShow?: $FixMe;
    renderPanelHeaderAction?: Function;
    selectedFilters?: $FixMe;
    stickyFooter?: JSX.Element | boolean;
    style?: object;
}

const FilterPanel = (props: FilterPanelProps) => {
    const [isSaveActive, setIsSaveActive] = useState(false);
    const [currentMultiVal, setCurrentMultiVal] = useState<{
        [key: string]: any;
    }>({});
    const [isDatePickerDrawerVisible, setDatePickerDrawerVisible] =
        useState(false);

    const handleCustomDateChange = (...args: any[]) => {
        setDatePickerDrawerVisible(false);
        props.handleDrawerSelect?.('');
        props.onCustomDateChange?.(...args);
    };

    const handleMultiSelectSave = () => {
        const { key, shouldUpdateRoute, value } =
            currentMultiVal[props.filterTypeToShow || ''];

        props.onFilterChange?.(key, value, shouldUpdateRoute);
        setIsSaveActive(false);
        setCurrentMultiVal({});
    };

    const handleFilterChange = (
        key: string | number,
        val?: string,
        shouldUpdateRoute?: boolean,
    ) => {
        if (MULTISELECT_MAP[key]) {
            setCurrentMultiVal({
                [key]: {
                    key,
                    shouldUpdateRoute,
                    value: val,
                },
            });

            setIsSaveActive(!isEqual(props.selectedFilters?.[key], val));
        } else {
            props.onFilterChange?.(key, val, shouldUpdateRoute);
        }
    };

    const headerTitle = getFilterPanelTitle(
        props.filterTypeToShow,
        isDatePickerDrawerVisible,
    );

    const filterProps = {
        ...props,
        onFilterChange: handleFilterChange,
        onCustomDateChange: handleCustomDateChange,
        isDatePickerDrawerVisible: isDatePickerDrawerVisible,
        setDatePickerDrawerVisible: (
            val: boolean | ((prevState: boolean) => boolean),
        ) => setDatePickerDrawerVisible(val),
    };

    return (
        <div
            className={classnames('filter-panel-and-title-container', {
                'filter-panel-and-title-container--mobile': props.isMobile,
            })}
        >
            <FilterPanelHeader
                isMobile={props.isMobile}
                filterTitle={headerTitle}
                filterTypeToShow={props.filterTypeToShow}
                isDatePickerDrawerVisible={isDatePickerDrawerVisible}
                onCloseDatePickerDrawer={setDatePickerDrawerVisible}
                handleDrawerSelect={props.handleDrawerSelect}
            >
                {props.renderPanelHeaderAction &&
                    props.renderPanelHeaderAction()}
            </FilterPanelHeader>
            <FilterPanelMain isMobile={props.isMobile}>
                {props.filterTypeToShow ? (
                    <Filters {...filterProps} />
                ) : (
                    <FilterPanelDrawers {...filterProps} />
                )}
            </FilterPanelMain>
            <FilterPanelFooter isMobile={props.isMobile}>
                {props.filterTypeToShow === 'languages' && (
                    <SaveButton
                        isActive={isSaveActive}
                        onClick={handleMultiSelectSave}
                    />
                )}
                {props.stickyFooter && props.stickyFooter}
            </FilterPanelFooter>
        </div>
    );
};

export default FilterPanel;
